import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Card, Button, Spinner, Alert, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import {
  fetchChatbots,
  setFilters,
  setPage,
  deleteChatbot,
} from "../../features/ai_chatbots/aiChatbotsSlice";
import AIChatbotsFilter from "./components/AIChatbotsFilter";
import AIChatbotDetailModal from "./components/AIChatbotDetailModal";
import AIChatbotWidgetModal from "./components/AIChatbotWidgetModal";
import AIChatbotSessions from "./components/AIChatbotSessions";
import AIChatbotFormModal from "./components/AIChatbotFormModal";
import ConfirmationModal from "../../components/common/ConfirmationModal";
import Pagination from "../../components/common/Pagination";
import { FaPlus, FaEdit, FaRobot, FaEye, FaCode, FaTrash, FaCog, FaComments } from "react-icons/fa";
// navigate no longer used
import AIChatbotTestModal from "./components/AIChatbotTestModal";
import { Icon } from "../../components/common/Icon";
import { useTranslation } from "react-i18next";
// Removed sessions tab view from this page; sessions will open per-card
import { isAdmin, isAgencyOwner } from "../../utils/permissions";
// companiesService already imported below
import companiesService from "../../features/companies/companiesService";
import usersService from "../../features/users/usersService";

const AIChatbotsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { chatbots, isLoading, error, totalCount, page, limit, filters } =
    useSelector((state: RootState) => state.aiChatbots);

  // Settings'ten seçilen şirket bilgilerini al
  const { selectedAgencyId, selectedClientId } = useSelector(
    (state: RootState) => state.settings
  );
  
  // Kullanıcı bilgilerini al
  const { user } = useSelector((state: RootState) => state.auth);
  
  // Kullanıcı rollerini kontrol et
  const userRoles = user?.roles || [];
  const canManageMD = isAdmin(user) || isAgencyOwner(user);
  
  // Debug için console.log
  console.log('AIChatbotsPage - User roles:', userRoles);
  console.log('AIChatbotsPage - Can manage MD:', canManageMD);
  
  const { t } = useTranslation();

  // Removed multi-tab UI; only chatbots list remains
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showWidgetModal, setShowWidgetModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedChatbotId, setSelectedChatbotId] = useState<number | null>(
    null
  );
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showSessionsModal, setShowSessionsModal] = useState(false);
  const [myCompanyId, setMyCompanyId] = useState<number | undefined>(undefined);

  useEffect(() => {
    const roles = user?.roles || [];
    const isOwner = roles.includes("company_owner");
    const isSpecialist = roles.includes("company_specialist");
    (async () => {
      if (selectedClientId || selectedAgencyId || myCompanyId !== undefined) return;
      try {
        if (isOwner) {
          const c = await companiesService.getMyCompany();
          setMyCompanyId(c?.id);
        } else if (isSpecialist) {
          const list = await usersService.getMyCompanies();
          const single = Array.isArray(list) ? list.find((c: any) => c.company_type === "single") : undefined;
          if (single?.id) setMyCompanyId(single.id);
        }
      } catch {
        setMyCompanyId(undefined);
      }
    })();
  }, [user, selectedClientId, selectedAgencyId, myCompanyId]);

  const roles = user?.roles || [];
  const isCompanyRole = roles.includes("company_owner") || roles.includes("company_specialist");

  const effectiveCompanyId = useMemo(() => {
    return selectedClientId || selectedAgencyId || (isCompanyRole ? myCompanyId : undefined);
  }, [selectedClientId, selectedAgencyId, isCompanyRole, myCompanyId]);

  useEffect(() => {
    const initialFilters: any = { ...filters };
    if (effectiveCompanyId) {
      initialFilters.company_id = effectiveCompanyId;
    } else {
      delete initialFilters.company_id;
    }
    // Ensure we fetch marketing module chatbots on this page
    initialFilters.module = 'marketing';
    dispatch(setFilters(initialFilters));
    dispatch(fetchChatbots({ ...initialFilters, page, limit }));
  }, [dispatch, effectiveCompanyId, page, limit]);

  const handlePageChange = (newPage: number) => {
    dispatch(setPage(newPage));
  };

  const handleFilterChange = (newFilters: any) => {
    const enforced: any = { ...newFilters };
    if (isCompanyRole && myCompanyId) enforced.company_id = myCompanyId;
    enforced.module = 'marketing';
    dispatch(setPage(1));
    dispatch(setFilters(enforced));
    const fetchParams = { ...enforced, page: 1, limit };
    dispatch(fetchChatbots(fetchParams));
  };

  const handleOpenCreateModal = () => {
    if (!canManageMD) return;
    setShowCreateModal(true);
  };

  const handleViewDetails = (chatbotId: number) => {
    setSelectedChatbotId(chatbotId);
    setShowDetailModal(true);
  };

  const handleGetWidget = (chatbotId: number) => {
    setSelectedChatbotId(chatbotId);
    setShowWidgetModal(true);
  };

  const handleDeleteClick = (chatbotId: number) => {
    setSelectedChatbotId(chatbotId);
    setShowDeleteModal(true);
  };

  const handleEditClick = (chatbotId: number) => {
    setSelectedChatbotId(chatbotId);
    setShowEditModal(true);
  };

  const handleTestClick = (chatbotId: number) => {
    setSelectedChatbotId(chatbotId);
    setShowTestModal(true);
  };

  const handleSessionsClick = (chatbotId: number) => {
    setSelectedChatbotId(chatbotId);
    setShowSessionsModal(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "success";
      case "draft":
        return "secondary";
      case "paused":
        return "warning";
      default:
        return "danger";
    }
  };

  const renderChatbotsTab = () => {
    if (isLoading) {
      return (
        <div className="loading-container">
          <div className="loading-content">
            <Spinner animation="border" className="loading-spinner" />
            <p className="loading-text">{t("loading_chatbots")}...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <Alert className="error-alert">
          <div className="d-flex align-items-center">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            {error}
          </div>
        </Alert>
      );
    }

    if (chatbots.length > 0) {
      return (
        <>
          <Row className="chatbots-grid">
            {chatbots.map((chatbot) => (
              <Col key={chatbot.id} lg={4} md={6} className="mb-4">
                <Card className="chatbot-card">
                  <Card.Header className="chatbot-header">
                    <div className="chatbot-title">
                      <div className="chatbot-icon">
                        <Icon icon={FaRobot} />
                      </div>
                      <div className="chatbot-info">
                        <h5 className="chatbot-name">{chatbot.name}</h5>
                        <span
                          className={`status-badge ${chatbot.is_active ? "active" : "inactive"}`}
                        >
                          <span className="status-indicator"></span>
                          {chatbot.is_active ? t("active") : t("inactive")}
                        </span>
                      </div>
                    </div>
                  </Card.Header>

                  <Card.Body className="chatbot-body">
                    <p className="chatbot-description">
                      {chatbot.description || t("no_description")}
                    </p>

                    <div className="chatbot-details">
                      <div className="detail-item">
                        <span className="detail-label">{t("model")}:</span>
                        <span className="detail-value">{chatbot.model}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">{t("status")}:</span>
                        <span
                          className={`status-badge-small ${getStatusColor(chatbot.status)}`}
                        >
                          {chatbot.status.charAt(0).toUpperCase() +
                            chatbot.status.slice(1)}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">{t("created")}:</span>
                        <span className="detail-value">
                          {new Date(chatbot.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </Card.Body>

                  <Card.Footer className="chatbot-footer">
                    <div className="action-buttons">
                      {/* Sessions button moved onto card */}
                      <Button
                        size="sm"
                        className="action-btn neutral"
                        onClick={() => handleSessionsClick(chatbot.id)}
                      >
                        <Icon icon={FaComments} className="me-1" />
                        {t("AI Chatbot Sessions")}
                      </Button>
                      <Button
                        size="sm"
                        className="action-btn info"
                        onClick={() => handleViewDetails(chatbot.id)}
                      >
                        <Icon icon={FaEye} className="me-1" />
                        {t("details")}
                      </Button>
                      {canManageMD && (
                        <Button
                          size="sm"
                          className="action-btn primary"
                          onClick={() => handleEditClick(chatbot.id)}
                        >
                          <Icon icon={FaEdit} className="me-1" />
                          {t("edit")}
                        </Button>
                      )}
                      {canManageMD && (
                        <Button
                          size="sm"
                          className="action-btn success"
                          onClick={() => handleGetWidget(chatbot.id)}
                        >
                          <Icon icon={FaCode} className="me-1" />
                          {t("widget")}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        className="action-btn neutral"
                        onClick={() => handleTestClick(chatbot.id)}
                      >
                        <Icon icon={FaRobot} className="me-1" />
                        {t("test")}
                      </Button>
                      {canManageMD && (
                        <Button
                          size="sm"
                          className="action-btn danger delete-btn"
                          onClick={() => handleDeleteClick(chatbot.id)}
                        >
                          <Icon icon={FaTrash} className="me-1" />
                          {t("delete")}
                        </Button>
                      )}
                    </div>
                  </Card.Footer>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Pagination */}
          <div className="pagination-container">
            <div className="pagination-info">
              {t("showing")} {chatbots.length} {t("of")} {totalCount}{" "}
              {t("chatbots")}
            </div>
            <Pagination
              currentPage={page}
              totalCount={totalCount}
              pageSize={limit}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      );
    }

    return (
      <div className="empty-state">
        <div className="empty-icon">
          <Icon icon={FaRobot} />
        </div>
        <h3 className="empty-title">{t("no_chatbots_found")}</h3>
        <p className="empty-description">
          {t("create_your_first_ai_chatbot_to_get_started")}
        </p>
        {canManageMD && (
          <Button className="create-btn" onClick={handleOpenCreateModal}>
            <Icon icon={FaPlus} className="me-2" />
            {t("create_new_chatbot")}
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="ai-chatbots-page">
      <Container fluid>
        {/* Modern Header */}
        <div className="page-header">
          <div className="header-content">
            <div className="header-info">
              <div className="header-icon">
                <Icon icon={FaRobot} />
              </div>
              <div className="header-text">
                <h1 className="page-title">{t("ai_chatbots")}</h1>
                <p className="page-subtitle">
                  {t("manage_your_ai_powered_chatbot_assistants")}
                </p>
              </div>
            </div>
            <div className="header-actions">
              {canManageMD && (
                <Button className="create-btn" onClick={handleOpenCreateModal}>
                  <Icon icon={FaPlus} className="me-2" />
                  {t("create_new_chatbot")}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Removed Tabs: sessions moved to per-card action */}

        {/* Filters Card */}
        <Card className="filter-card">
          <Card.Header className="card-header-custom">
            <div className="section-header">
              <div className="section-icon filter-icon">
                <Icon icon={FaCog} />
              </div>
              <h5 className="section-title">{t("chatbot_filters")}</h5>
            </div>
          </Card.Header>
          <Card.Body className="card-body-custom">
            <AIChatbotsFilter onFilterChange={handleFilterChange} />
          </Card.Body>
        </Card>

        {/* Chatbots Content */}
        {renderChatbotsTab()}

        {/* Modals */}
        {showCreateModal && (
          <AIChatbotFormModal
            show={showCreateModal}
            onHide={() => setShowCreateModal(false)}
            defaultCompanyId={selectedClientId || selectedAgencyId || myCompanyId}
            defaultModule="marketing"
          />
        )}

        {showDetailModal && (
          <AIChatbotDetailModal
            show={showDetailModal}
            onHide={() => setShowDetailModal(false)}
            chatbotId={selectedChatbotId}
          />
        )}

        {showWidgetModal && (
          <AIChatbotWidgetModal
            show={showWidgetModal}
            onHide={() => setShowWidgetModal(false)}
            chatbotId={selectedChatbotId}
          />
        )}

        {showEditModal && (
          <AIChatbotFormModal
            show={showEditModal}
            onHide={() => setShowEditModal(false)}
            chatbot={chatbots.find((c) => c.id === selectedChatbotId)}
            onSuccess={() => {
              setShowEditModal(false);
              dispatch(fetchChatbots({ ...filters, page, limit }));
            }}
          />
        )}

        <ConfirmationModal
          show={showDeleteModal}
          onCancel={() => setShowDeleteModal(false)}
          onConfirm={() => {
            if (selectedChatbotId) {
              dispatch(deleteChatbot(selectedChatbotId))
                .unwrap()
                .then(() => {
                  setShowDeleteModal(false);
                  dispatch(fetchChatbots({ ...filters, page, limit }));
                })
                .catch((error) => {
                  console.error("Error deleting chatbot:", error);
                });
            }
          }}
          title={t("delete_chatbot")}
          message={t("are_you_sure_delete_chatbot")}
          confirmText={t("delete")}
        />

        {showTestModal && (
          <AIChatbotTestModal
            show={showTestModal}
            onHide={() => setShowTestModal(false)}
            chatbot={
              selectedChatbotId
                ? chatbots.find((c) => c.id === selectedChatbotId) || null
                : null
            }
          />
        )}

        {showSessionsModal && selectedChatbotId && (
          <Modal show={showSessionsModal} onHide={() => setShowSessionsModal(false)} size="xl">
            <Modal.Header closeButton>
              <Modal.Title>
                {t("AI Chatbot Sessions")} – {chatbots.find((c) => c.id === selectedChatbotId)?.name}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <AIChatbotSessions defaultChatbotId={selectedChatbotId} hideChatbotSelect />
            </Modal.Body>
          </Modal>
        )}
      </Container>
      <style>
        {`
.ai-chatbots-page {
  --bg-primary: #ffffff;
  --bg-secondary: #f8fafc;
  --bg-tertiary: #f1f5f9;
  --text-primary: #0f172a;
  --text-secondary: #475569;
  --text-muted: #64748b;
  --border-color: #e2e8f0;
  --shadow-light: 0 1px 3px rgba(0, 0, 0, 0.1);
  --shadow-medium: 0 4px 6px rgba(0, 0, 0, 0.07);
  --shadow-hover: 0 8px 25px rgba(0, 0, 0, 0.15);
  --accent-primary: #3b82f6;
  --accent-secondary: #8b5cf6;
  --success-color: #10b981;
  --warning-color: #f59e0b;
  --danger-color: #ef4444;
  --info-color: #06b6d4;
  --chip-bg: rgba(2, 6, 23, 0.04);
  --chip-border: rgba(2, 6, 23, 0.08);
  padding: 24px;
  background: var(--bg-secondary);
  min-height: 100vh;
}

/* Tab Styles */
.tabs-container {
  margin-bottom: 24px;
}

.custom-tabs {
  border-bottom: 2px solid var(--border-color);
}

.custom-tabs .nav-link {
  color: var(--text-secondary);
  border: none;
  padding: 12px 20px;
  font-weight: 600;
  transition: all 0.2s ease;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
  background: transparent;
}

.custom-tabs .nav-link:hover {
  color: var(--accent-primary);
  border-bottom-color: var(--accent-primary);
  opacity: 0.8;
}

.custom-tabs .nav-link.active {
  color: var(--accent-primary);
  border-bottom: 3px solid var(--accent-primary);
  background: transparent;
}

.tab-item {
  display: flex;
  align-items: center;
  font-size: 15px;
}

/* Dark Theme */
[data-bs-theme="dark"] .ai-chatbots-page {
  --bg-primary: #1e293b;
  --bg-secondary: #0f172a;
  --bg-tertiary: #334155;
  --text-primary: #f8fafc;
  --text-secondary: #cbd5e1;
  --text-muted: #94a3b8;
  --border-color: #475569;
  --shadow-light: 0 1px 3px rgba(0, 0, 0, 0.3);
  --shadow-medium: 0 4px 6px rgba(0, 0, 0, 0.2);
  --shadow-hover: 0 8px 25px rgba(0, 0, 0, 0.4);
  --chip-bg: rgba(255, 255, 255, 0.05);
  --chip-border: rgba(255, 255, 255, 0.12);
}

/* Header Styles */
.page-header {
  margin-bottom: 24px;
  padding: 20px 24px;
  background: var(--bg-primary);
  border-radius: 16px;
  box-shadow: var(--shadow-light);
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-secondary) 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
  transition: all 0.3s ease;
}

.header-icon:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.35);
}

.header-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.2;
}

.page-subtitle {
  font-size: 14px;
  color: var(--text-muted);
  margin: 0;
  font-weight: 500;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.create-btn {
  background: linear-gradient(135deg, var(--accent-primary), var(--accent-secondary)) !important;
  border: none !important;
  color: white !important;
  padding: 10px 20px !important;
  border-radius: 10px !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  transition: all 0.2s ease !important;
  display: flex;
  align-items: center;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
}

.create-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.35) !important;
  color: white !important;
}

/* Filter Card */
.filter-card {
  background: var(--bg-primary) !important;
  border: 1px solid var(--border-color) !important;
  box-shadow: var(--shadow-light) !important;
  margin-bottom: 24px !important;
  border-radius: 16px !important;
  overflow: hidden;
  transition: all 0.3s ease;
}

.filter-card:hover {
  box-shadow: var(--shadow-medium) !important;
}

.card-header-custom {
  background: var(--bg-tertiary) !important;
  border-bottom: 1px solid var(--border-color) !important;
  padding: 16px 20px !important;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.section-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.filter-icon {
  background: linear-gradient(135deg, var(--info-color), #0891b2);
  color: white;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.card-body-custom {
  padding: 20px !important;
  background: var(--bg-primary) !important;
}

/* Loading States */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 20px;
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-light);
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.loading-spinner {
  color: var(--accent-primary) !important;
  width: 3rem !important;
  height: 3rem !important;
}

.loading-text {
  color: var(--text-muted);
  font-size: 16px;
  font-weight: 500;
  margin: 0;
}

/* Error Alert */
.error-alert {
  background: rgba(239, 68, 68, 0.1) !important;
  border: 1px solid rgba(239, 68, 68, 0.2) !important;
  color: var(--danger-color) !important;
  border-radius: 12px !important;
  padding: 16px 20px !important;
  margin-bottom: 24px !important;
}

/* Chatbots Grid */
.chatbots-grid {
  margin-bottom: 32px;
}

.chatbot-card {
  background: var(--bg-primary) !important;
  border: 1px solid var(--border-color) !important;
  border-radius: 16px !important;
  box-shadow: var(--shadow-light) !important;
  transition: all 0.3s ease !important;
  overflow: hidden;
  height: 100%;
}

.chatbot-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-hover) !important;
}

.chatbot-header {
  background: var(--bg-tertiary) !important;
  border-bottom: 1px solid var(--border-color) !important;
  padding: 16px 20px !important;
}

.chatbot-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.chatbot-icon {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, var(--accent-primary), var(--accent-secondary));
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  flex-shrink: 0;
}

.chatbot-info {
  flex: 1;
  min-width: 0;
}

.chatbot-name {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 4px 0;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-badge.active {
  background: rgba(16, 185, 129, 0.1);
  color: var(--success-color);
  border: 1px solid rgba(16, 185, 129, 0.2);
}

.status-badge.inactive {
  background: rgba(100, 116, 139, 0.1);
  color: var(--text-muted);
  border: 1px solid rgba(100, 116, 139, 0.2);
}

.status-indicator {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.chatbot-body {
  padding: 20px !important;
  background: var(--bg-primary) !important;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.chatbot-description {
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 16px;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.chatbot-details {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--chip-bg);
  border: 1px solid var(--chip-border);
}

.detail-label {
  color: var(--text-muted);
  font-weight: 500;
}

.detail-value {
  color: var(--text-primary);
  font-weight: 600;
}

.status-badge-small {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid transparent;
}

.status-badge-small.success {
  background: rgba(16, 185, 129, 0.1);
  color: var(--success-color);
  border-color: rgba(16, 185, 129, 0.25);
}

.status-badge-small.secondary {
  background: rgba(100, 116, 139, 0.1);
  color: var(--text-muted);
  border-color: rgba(100, 116, 139, 0.25);
}

.status-badge-small.warning {
  background: rgba(245, 158, 11, 0.1);
  color: var(--warning-color);
  border-color: rgba(245, 158, 11, 0.25);
}

.status-badge-small.danger {
  background: rgba(239, 68, 68, 0.1);
  color: var(--danger-color);
  border-color: rgba(239, 68, 68, 0.25);
}

.chatbot-card:hover .detail-item {
  border-color: var(--border-color);
}

.chatbot-footer {
  background: var(--bg-tertiary) !important;
  border-top: 1px solid var(--border-color) !important;
  padding: 16px 20px !important;
}

.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-start;
}

.action-btn {
  padding: 6px 12px !important;
  border-radius: 8px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  transition: all 0.2s ease !important;
  display: flex;
  align-items: center;
  gap: 4px;
  border: 1px solid var(--border-color) !important;
  background: var(--bg-primary) !important;
  flex: 1;
  min-width: fit-content;
  justify-content: center;
}

.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-light);
}

.action-btn.primary {
  color: var(--accent-primary) !important;
  border-color: color-mix(in srgb, var(--accent-primary) 40%, transparent) !important;
}

.action-btn.primary:hover {
  background: color-mix(in srgb, var(--accent-primary) 12%, transparent) !important;
}

.action-btn.success {
  color: var(--success-color) !important;
  border-color: color-mix(in srgb, var(--success-color) 40%, transparent) !important;
}

.action-btn.success:hover {
  background: color-mix(in srgb, var(--success-color) 12%, transparent) !important;
}

.action-btn.info {
  color: var(--info-color) !important;
  border-color: color-mix(in srgb, var(--info-color) 40%, transparent) !important;
}

.action-btn.info:hover {
  background: color-mix(in srgb, var(--info-color) 12%, transparent) !important;
}

.action-btn.neutral {
  color: var(--text-secondary) !important;
}

.action-btn.danger {
  color: var(--danger-color) !important;
  border-color: color-mix(in srgb, var(--danger-color) 40%, transparent) !important;
}

.action-btn.danger:hover {
  background: color-mix(in srgb, var(--danger-color) 12%, transparent) !important;
}

.action-btn.delete-btn:hover {
  background: var(--danger-color) !important;
  color: white !important;
  border-color: var(--danger-color) !important;
}

/* Pagination */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-light);
  margin-top: 24px;
}

.pagination-info {
  color: var(--text-muted);
  font-size: 14px;
  font-weight: 500;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-light);
  text-align: center;
}

.empty-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, var(--accent-primary), var(--accent-secondary));
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 32px;
  margin-bottom: 24px;
  opacity: 0.8;
}

.empty-title {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.empty-description {
  font-size: 16px;
  color: var(--text-muted);
  margin-bottom: 32px;
  max-width: 400px;
  line-height: 1.5;
}

/* Responsive Design */
@media (max-width: 768px) {
  .ai-chatbots-page {
    padding: 16px;
  }

  .page-header {
    padding: 16px 20px;
    margin-bottom: 20px;
  }

  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .header-info {
    gap: 12px;
  }

  .header-icon {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }

  .page-title {
    font-size: 20px;
  }

  .page-subtitle {
    font-size: 13px;
  }

  .create-btn {
    padding: 8px 16px !important;
    font-size: 13px !important;
  }

  .filter-card {
    margin-bottom: 20px;
  }

  .chatbots-grid {
    margin-bottom: 20px;
  }

  .chatbot-card {
    margin-bottom: 16px;
  }

  .chatbot-header {
    padding: 12px 16px;
  }

  .chatbot-name {
    font-size: 16px;
  }

  .chatbot-body {
    padding: 12px 16px;
  }

  .chatbot-footer {
    padding: 12px 16px;
  }

  .action-buttons {
    flex-direction: column;
    gap: 8px;
  }

  .action-btn {
    width: 100%;
    justify-content: center;
  }

  .pagination-container {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }

  .empty-state {
    padding: 32px 16px;
  }

  .empty-title {
    font-size: 20px;
  }

  .empty-description {
    font-size: 14px;
  }
}

@media (max-width: 576px) {
  .ai-chatbots-page {
    padding: 12px;
  }

  .page-header {
    padding: 12px 16px;
    margin-bottom: 16px;
  }

  .header-icon {
    width: 36px;
    height: 36px;
    font-size: 16px;
  }

  .page-title {
    font-size: 18px;
  }

  .page-subtitle {
    font-size: 12px;
  }

  .create-btn {
    padding: 6px 12px !important;
    font-size: 12px !important;
  }

  .chatbot-card {
    margin-bottom: 12px;
  }

  .chatbot-header {
    padding: 10px 12px;
  }

  .chatbot-name {
    font-size: 15px;
  }

  .chatbot-body {
    padding: 10px 12px;
  }

  .chatbot-footer {
    padding: 10px 12px;
  }

  .detail-item {
    font-size: 13px;
  }

  .action-btn {
    padding: 6px 12px;
    font-size: 12px;
  }

  .empty-icon {
    font-size: 48px;
  }

  .empty-title {
    font-size: 18px;
  }

  .empty-description {
    font-size: 13px;
  }
}

/* Print Styles */
@media print {
  .ai-chatbots-page {
    background: white !important;
    padding: 0;
  }

  .page-header {
    background: white !important;
    box-shadow: none !important;
    border: 1px solid #ddd !important;
  }

  .header-actions,
  .action-buttons,
  .pagination-container {
    display: none !important;
  }

  .chatbot-card {
    break-inside: avoid;
    margin-bottom: 16px;
    box-shadow: none !important;
    border: 1px solid #ddd !important;
  }

  .filter-card {
    display: none !important;
  }
}

/* High Contrast Mode */
@media (prefers-contrast: high) {
  .ai-chatbots-page {
    --border-color: #000000;
    --shadow-light: none;
    --shadow-medium: none;
    --shadow-hover: none;
  }

  .page-header,
  .filter-card,
  .chatbot-card {
    border: 2px solid var(--border-color) !important;
    box-shadow: none !important;
  }

  .create-btn,
  .action-btn {
    border: 2px solid currentColor !important;
  }

  .status-badge,
  .status-badge-small {
    border: 1px solid currentColor;
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .ai-chatbots-page *,
  .ai-chatbots-page *::before,
  .ai-chatbots-page *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    transform: none !important;
  }

  .header-icon:hover,
  .create-btn:hover,
  .action-btn:hover,
  .chatbot-card:hover {
    transform: none !important;
  }
}

/* Focus Styles for Accessibility */
.create-btn:focus,
.action-btn:focus {
  outline: 2px solid var(--accent-primary);
  outline-offset: 2px;
}

/* Hover Effects */
.chatbot-card:hover .chatbot-icon {
  transform: scale(1.1);
}

.chatbot-card:hover .chatbot-name {
  color: var(--accent-primary);
}

/* Animation Classes */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.chatbot-card {
  animation: fadeIn 0.3s ease-out;
}

.chatbot-card:nth-child(1) { animation-delay: 0ms; }
.chatbot-card:nth-child(2) { animation-delay: 50ms; }
.chatbot-card:nth-child(3) { animation-delay: 100ms; }
.chatbot-card:nth-child(4) { animation-delay: 150ms; }
.chatbot-card:nth-child(5) { animation-delay: 200ms; }
.chatbot-card:nth-child(6) { animation-delay: 250ms; }

/* Custom Scrollbar */
.ai-chatbots-page::-webkit-scrollbar {
  width: 8px;
}

.ai-chatbots-page::-webkit-scrollbar-track {
  background: var(--bg-tertiary);
  border-radius: 4px;
}

.ai-chatbots-page::-webkit-scrollbar-thumb {
  background: var(--border-color);
  border-radius: 4px;
}

.ai-chatbots-page::-webkit-scrollbar-thumb:hover {
  background: var(--text-muted);
}
        `}
      </style>
    </div>
  );
};
export default AIChatbotsPage;